/**
 * Why the call did not happen, in a box that can hold a sentence.
 *
 * It used to live inside the status pill, which is a 34px capsule with a
 * blinking dot in it. Every one of these messages is at least one full sentence
 * and half of them are two - the do-not-call refusal explains that somebody
 * asked us to stop, the Twilio ones name a code and say where to go and look -
 * so the pill wrapped, the dot ended up floating beside the middle of a
 * paragraph, and the whole thing was set in bold red because that is what a
 * status pill does to its text. A wall of shouting is the one shape an
 * explanation must not have: the operator has to READ this, usually while
 * somebody is waiting, and bold centred red is what people skip.
 *
 * The file is `CallErrorBox` rather than `CallError` because `callError.ts`
 * next door already owns that name in every sense that matters: it is the
 * classifier, and on a case-insensitive filesystem the two files would be one
 * module as far as the compiler is concerned.
 *
 * So: left-aligned, ordinary weight, ordinary leading, an icon that says what
 * kind of stop this is, and a tint rather than a shout. The pill keeps its job
 * and gets a short line back (`errorStatusKey`).
 *
 * THE ICON IS NOT DECORATION. A refusal and a fault are different news. The
 * do-not-call list is the app doing exactly what it was told, and drawing a
 * warning triangle on it says something went wrong when nothing did; a blocked
 * microphone is the operator's own browser, and a crossed-out microphone points
 * at the thing to go and fix. Everything else really is a fault, and gets the
 * triangle.
 */
import { Ban, Info, MicOff, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { errorMessageKey, twilioCode } from "./callError";

const ICON: Record<string, LucideIcon> = {
  suppressed: Ban,
  mic_denied: MicOff,
  // A takeover the server turned down is the call being somebody else's, or not
  // yet ready, or over - news about the call, not a fault to go and fix.
  takeover_taken: Info,
  takeover_elsewhere: Info,
  takeover_opening: Info,
  takeover_gone: Info,
  takeover_refused: Ban,
};

export function CallErrorBox({ error }: { error: string | null }) {
  const t = useT();
  // Keyed on the MESSAGE, not on the coarse verdict, so a microphone Twilio
  // refused (31401) draws the same crossed-out microphone as one the browser
  // refused. Anything else falls through to the triangle exactly as before.
  const key = errorMessageKey(error);
  const Icon = ICON[key] ?? TriangleAlert;

  return (
    // `role="alert"` rather than a plain box: the phase changes under a screen
    // reader that was last told the call was connecting, and the sentence that
    // replaces it is the only thing on the screen that says why it stopped.
    <div className="crm-callerr" role="alert">
      <Icon className="size-4" strokeWidth={2} />
      <p>{t(`calls.errors.${key}`, { code: twilioCode(error) })}</p>
    </div>
  );
}
